// @flow
import { useEffect, useState, useRef } from 'react'
import styled from 'styled-components'
import { nest } from 'd3-collection'
import { max } from 'd3-array'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/icon.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/flag.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/label.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/grid.css'
import './styles.scss'
import { useTransition, animated } from '@react-spring/web'
import arrowLeft from './img/arrow-left.svg'
import arrowLeftInvert from './img/arrow-left-invert.svg'

import halfTree from './img/tree-half.png'
import fullTree from './img/tree-full.png'
import emptyTree from './img/tree-empty.png'
import noTree from './img/no-tree.png'
import halfTreeSanta from './img/tree-half-santa.png'
import fullTreeSanta from './img/tree-full-santa.png'
import emptyTreeSanta from './img/tree-empty-santa.png'
import noTreeSanta from './img/no-tree-santa.png'

const treeImgs = {
  santa: [emptyTreeSanta, halfTreeSanta, fullTreeSanta, noTreeSanta],
  default: [emptyTree, halfTree, fullTree, noTree],
}

interface Santa {
  show: boolean
  row: number
}

interface Row {
  snow: number
  year: number
  day: number
}

interface Item {
  key: string
  values: any
  year: number
  snow24: number
  snow25: number
  snow26: number
  average: number
  max: number | undefined
  yearShort: string
  tree: number
  op: object
  trans: object
}

const Source = styled.p`
  padding-top: 40px;
  text-align: left;
  font-family: var(--font-plex);
`

export const Trees = ({
  dataSet,
  locale = 'fr',
  colorMode,
}: {
  dataSet: string[]
  locale: 'de' | 'fr'
  colorMode: 'dark' | 'light'
}) => {
  const treesContainerRef = useRef(null)
  const [nestedData, setNestedData] = useState<string[]>([])
  const [flipArrow, setFlipArrow] = useState(true)
  const [santa, setSanta] = useState<Santa>({
    show: false,
    row: 0,
  })

  const arrangeData = (data: Row[]) => {
    if (data.length % 7 === 0) {
      // setFlipArrow(false)
      data = data.filter((d) => d.year >= 1965)
    }

    const nestedData = nest()
      .key(function (d) {
        return d.year
      })
      .entries(data)

    const filteredData = nestedData.filter((d) => d.values.length === 3)
    const num = filteredData.length

    filteredData.forEach(function (d: Item, i) {
      d.year = +d.key
      d.snow24 = +d.values[0].snow
      d.snow25 = +d.values[1].snow
      d.snow26 = +d.values[2].snow
      d.average = (d.snow24 + d.snow25 + d.snow26) / 3
      d.max = max([d.snow24, d.snow25, d.snow26])
      d.yearShort = d.key.slice(2, 4)

      if (d.snow24 === -1 || d.snow25 === -1 || d.snow26 === -1) {
        d.tree = 3
      } else {
        if (d.snow24 + d.snow25 + d.snow26 < 1) {
          d.tree = 0
        } else if (d.snow24 > 0 && d.snow25 > 0 && d.snow26 > 0) {
          d.tree = 2
        } else {
          d.tree = 1
        }
      }

      d.op = {
        range: [i / num, i / num + 1 / num],
        output: [0, 1],
        extrapolate: 'clamp',
      }

      d.trans = {
        range: [i / num, i / num + 1 / num],
        output: [-20, 0],
        extrapolate: 'clamp',
      }
    })
    return filteredData.sort((a, b) => a.year - b.year)
  }

  useEffect(() => {
    setNestedData(arrangeData(dataSet))
  }, [dataSet])

  useEffect(() => {
    if (santa.show) {
      setTimeout(() => {
        setSanta({
          show: false,
          row: 0,
        })
      }, 10000)
    }
  }, [santa.show])

  const transitions = useTransition(nestedData, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    config: { duration: 2500 },
    delay: 100,
  })

  const santaTreeId = Math.floor(Math.random() * nestedData.length)

  return (
    <div className='trees'>
      <div className='ui unstackable grid'>
        <div className='four column row center aligned'>
          <div className='column'>
            <img
              width='100%'
              src={fullTree}
              alt='sapin légèrement enneigé'
              className='single legend tree'
            />
            <p className='legend'>
              {locale === 'fr'
                ? 'Trois jours de Noël blanc'
                : 'Drei weisse Weihnachtstage'}
            </p>
          </div>
          <div className='column'>
            <img
              width='100%'
              src={halfTree}
              alt='sapin couvert aux deux tiers de neige'
              className='single legend tree'
            />
            <p className='legend'>
              {locale === 'fr'
                ? 'Au moins un jour avec neige'
                : 'Mindestens ein Tag mit Schnee'}
            </p>
          </div>
          <div className='column'>
            <img
              width='100%'
              src={emptyTree}
              alt='sapin sans neige'
              className='single legend tree'
            />
            <p className='legend'>
              {locale === 'fr'
                ? 'Trois jours sans neige'
                : 'Alle drei Tage ohne Schnee'}
            </p>
          </div>
          <div className='column'>
            <img
              width='100%'
              src={noTree}
              alt='sapin flou'
              className='single legend tree'
            />
            <p className='legend'>
              {locale === 'fr'
                ? 'Pas de donnée pour cette année'
                : 'Keine Daten für dieses Jahr'}
            </p>
          </div>
        </div>
        <div className='one column row'>
          {santa.show && (
            <img
              style={{ top: santa.row }}
              src={require('../../images/santa.gif')}
              className='santa_anim'
              alt='Père Noël'
            />
          )}
          <div className='column' style={{ zIndex: 1 }} ref={treesContainerRef}>
            {transitions(({ opacity }, item, t, i) => (
              <animated.div
                style={{
                  display: 'inline-block',
                  opacity: opacity.to(item.op),
                  transform: opacity
                    .to(item.trans)
                    .to((y) => `translate3d(0,${y}px,0)`),
                }}
                className='tree container'
                key={`tree-container-${i}`}
              >
                <img
                  width='100%'
                  src={
                    santaTreeId === i
                      ? treeImgs.santa[item.tree]
                      : treeImgs.default[item.tree]
                  }
                  className={`single tree ${santaTreeId === i ? 'santa' : ''}`}
                  key={`tree-${i}`}
                  alt='Sapin'
                  onClick={(e) => {
                    if (santaTreeId === i && santa.show === false) {
                      setSanta({
                        show: true,
                        row:
                          e.target.getBoundingClientRect().top +
                          window.scrollY -
                          (treesContainerRef.current.getBoundingClientRect()
                            .top +
                            window.scrollY),
                      })
                    }
                  }}
                />
                <p>
                  {window.outerWidth < 599 && (
                    <span>
                      {i === 0 || i === nestedData.length - 1
                        ? item.year
                        : "'" + item.yearShort}
                    </span>
                  )}
                  {window.outerWidth >= 600 && item.year}
                </p>
              </animated.div>
            ))}
            <p className='forecast'>
              <img
                src={colorMode === 'dark' ? arrowLeftInvert : arrowLeft}
                alt='flèche'
                style={flipArrow ? { transform: 'scaleX(-1)' } : {}}
              />
              <span>{locale === 'fr' ? 'Prévision' : 'Prognose'}</span>
            </p>
          </div>
          <div className='column'>
            {/* <Source>
              {locale === 'fr' ? 'Source: MétéoSuisse' : 'Quelle: MeteoSchweiz'}
            </Source> */}
          </div>
        </div>
      </div>
    </div>
  )
}
